/**
 * Single source of truth for the product demo video.
 *
 * Both the homepage demo section (`/#demo`) and the dedicated watch page
 * (`/watch/anter-product-demo`) play the same file, and the watch page's
 * `VideoObject` structured data has to describe it exactly — duration, poster,
 * and chapter offsets all have to agree with the bytes we actually serve, or
 * Google drops the video result.
 *
 * `VIDEO_SRC` is deliberately versionless. The previous filename carried its
 * revision (`anter-builder-final-v2.mp4`), so every re-record changed the URL
 * and reset whatever crawl history the old one had accumulated. Google treats
 * the content URL as the video's identity, so keep this path stable and just
 * overwrite the file in place when the recording is updated — then bump
 * VIDEO_DURATION_SECONDS / VIDEO_UPLOAD_DATE and re-shoot the poster:
 *
 *   ffmpeg -ss 14 -i public/media/anter-product-demo.mp4 -frames:v 1 -q:v 2 \
 *     public/media/anter-product-demo-poster.jpg -y
 *
 * The mirrored values in `scripts/generate-sitemap.mjs` (video sitemap
 * extension) must be updated alongside — that script is plain Node and cannot
 * import this module.
 */

export const YOUTUBE_ID = "tPYBnf87PcI";
export const YOUTUBE_EMBED_URL = `https://www.youtube.com/embed/${YOUTUBE_ID}`;
export const VIDEO_WATCH_URL = `https://www.youtube.com/watch?v=${YOUTUBE_ID}`;

export const VIDEO_SRC = "/media/anter-product-demo.mp4";
export const VIDEO_POSTER = "/media/anter-product-demo-poster.jpg";

/** Watch page route — the canonical, indexable home for this video. */
export const VIDEO_WATCH_PATH = "/watch/anter-product-demo";

/** `ffprobe -show_entries format=duration` → 152.03s. */
export const VIDEO_DURATION_SECONDS = 152;

/** ISO 8601 for schema.org `duration`. */
export const VIDEO_DURATION_ISO = "PT2M32S";

/** Date the current recording was published, not the page's build date. */
export const VIDEO_UPLOAD_DATE = "2026-07-16";

export const VIDEO_WIDTH = 1280;
export const VIDEO_HEIGHT = 632;

export const VIDEO_NAME = "Anter Product Demo — Building and Running Managed AI Agents";

export const VIDEO_DESCRIPTION =
  "A full walkthrough of Anter, recorded from the live product. Watch a multi-agent system get built in the visual builder, then run end to end — triage routing, sub-agent delegation, MCP tool calls, and the execution trace and usage metrics behind every step.";

export type FlowEvent = {
  second: number;
  title: string;
  detail: string;
  tag: "Transfer" | "Delegate" | "Tool Call" | "Resolved";
};

/**
 * Chapters. Drives the on-page timeline on both pages and the `hasPart` Clip
 * nodes in the watch page's structured data, which is what Google uses to
 * render Key Moments under the video result.
 */
export const FLOW_EVENTS: FlowEvent[] = [
  {
    second: 4,
    title: "Agent workspace opens",
    detail: "Core orchestration and controls are shown live.",
    tag: "Tool Call",
  },
  {
    second: 12,
    title: "Cross-surface operations",
    detail: "The run moves across different product surfaces in one workflow.",
    tag: "Delegate",
  },
  {
    second: 22,
    title: "Execution traceability",
    detail: "Routing and transfer decisions stay inspectable step-by-step.",
    tag: "Transfer",
  },
  {
    second: 34,
    title: "Observability in context",
    detail: "Metrics and usage are visible where decisions are made.",
    tag: "Resolved",
  },
];

export function formatClock(second: number): string {
  const safe = Math.max(0, Math.floor(second));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
